import React, { useState, useRef } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { AdjustProductQuantity } from './stripe-link-builder-components'
import {
  useAdminStripeAvailableProducts,
  useAdminStripeAvailablePrices,
} from 'hooks/use-admin-stripe'
import { Price } from 'interfaces/price'
import { StripeProduct } from 'interfaces/stripe-product'
import SearchSelectTable from './search-select-table'

interface AddProductModalProps {
  show: boolean
  onHide: () => void
  handleAddProducts: (
    productId: string | number | undefined,
    productPriceId: string | number | undefined,
    productQuantity: number,
    productPrices: Price[],
    availableProducts: StripeProduct[],
    allowQuantityAdjusting: boolean
  ) => void
}

const AddProductModal: React.FC<AddProductModalProps> = ({
  show,
  onHide,
  handleAddProducts,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<StripeProduct | null>(
    null
  )
  const [selectedPrice, setSelectedPrice] = useState<Price | null>(null)
  const [localQuantity, setLocalQuantity] = useState(1)
  const [localAdjustable, setLocalAdjustable] = useState(false)
  const [productQuery, setProductQuery] = useState('')
  const [priceQuery, setPriceQuery] = useState('')
  const priceSearchRef = useRef<HTMLInputElement>(null)

  const {
    data: products,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
  } = useAdminStripeAvailableProducts()
  const availableProducts = products?.filter(
    (product) => product.metadata.is_subscription !== 'true'
  )

  const {
    data: prices,
    isLoading: isPricesLoading,
    isFetching: isPricesFetching,
  } = useAdminStripeAvailablePrices(selectedProduct?.id || '')

  const productPrices = prices?.filter((price) => !price.recurring)

  const handleProductSelect = (product: StripeProduct) => {
    setSelectedProduct(product)
    setProductQuery('')
    setPriceQuery('')
    if (priceSearchRef.current) {
      priceSearchRef.current.focus()
    }
  }

  const handlePriceSelect = (price: Price) => {
    setSelectedPrice(price)
    setPriceQuery('')
  }

  const handleAddAndClose = () => {
    if (
      !productPrices ||
      !selectedProduct ||
      !selectedPrice ||
      !availableProducts
    )
      return
    handleAddProducts(
      selectedProduct.id,
      selectedPrice.id,
      localQuantity,
      productPrices,
      availableProducts,
      localAdjustable
    )
    onHide()
    // Reset modal state
    setSelectedProduct(null)
    setSelectedPrice(null)
    setLocalQuantity(1)
    setLocalAdjustable(false)
    setProductQuery('')
    setPriceQuery('')
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Product</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SearchSelectTable<StripeProduct>
          title="Select a Product"
          placeholder="Search products..."
          query={productQuery}
          setQuery={setProductQuery}
          searchOptions={{
            keys: ['id', { name: 'name', weight: 2 }, 'description'],
            threshold: 0.3,
            fieldNormWeight: 1,
          }}
          data={availableProducts || []}
          selectedItem={selectedProduct}
          onSelect={handleProductSelect}
          onClear={() => {
            setSelectedProduct(null)
            setSelectedPrice(null)
            setProductQuery('')
          }}
          renderRow={(item: StripeProduct) => ({
            key: item.id,
            columns: [item.name, item.metadata.internal_description],
          })}
          columnHeaders={['Name', 'Description']}
          renderSelectedItem={(item: StripeProduct) => item.name}
          autoFocus
          isLoading={isProductsLoading || isProductsFetching}
        />

        {selectedProduct && (
          <>
            <SearchSelectTable<Price>
              title="Select a Price"
              placeholder="Search prices..."
              query={priceQuery}
              setQuery={setPriceQuery}
              searchOptions={{
                keys: ['id', 'unit_amount'],
                threshold: 0.3,
                fieldNormWeight: 1,
              }}
              data={productPrices || []}
              selectedItem={selectedPrice}
              onSelect={handlePriceSelect}
              onClear={() => {
                setSelectedPrice(null)
                setPriceQuery('')
              }}
              renderRow={(item: Price) => ({
                key: item.id,
                columns: [`$${(item.unit_amount || 0) / 100}`],
              })}
              columnHeaders={['Price']}
              renderSelectedItem={(item: Price) =>
                `$${(item.unit_amount || 0) / 100}`
              }
              isLoading={isPricesLoading || isPricesFetching}
            />

            {selectedPrice && (
              <div className="mb-4">
                <AdjustProductQuantity
                  productQuantity={localQuantity}
                  setProductQuantity={setLocalQuantity}
                  allowQuantityAdjusting={localAdjustable}
                  setAllowQuantityAdjusting={setLocalAdjustable}
                />
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleAddAndClose}
          disabled={!selectedProduct || !selectedPrice}
        >
          Add Product
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddProductModal
